import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError } from "../api/errors";
import { imagesApi } from "../api/images";
import type { ProductImage } from "../api/types";
import { useProductRefresh } from "./useProduct";

/**
 * Enable/disable, reorder and remove for the draft's generated images.
 *
 * Every one of them carries the draft version and reloads afterwards, so the
 * next operation uses a token the server will accept. Nothing is applied
 * optimistically: an image that appears to move and then springs back is worse
 * than one that takes a moment to move.
 */
export function useImageMutations(productId: string, draftVersion: number | null) {
  const refresh = useProductRefresh(productId);
  const [error, setError] = useState<unknown>(null);

  function settle(caught?: unknown) {
    setError(caught ?? null);
    // Success moves the version; a stale conflict proves the local copy is
    // behind. Both need the server's current state before anything else runs.
    return refresh();
  }

  const setEnabled = useMutation({
    mutationFn: (input: { imageId: string; enabled: boolean }) => {
      if (draftVersion === null) throw new Error("no editable draft");
      return imagesApi.updateImage(productId, input.imageId, {
        expected_version: draftVersion,
        enabled: input.enabled,
      });
    },
    onSuccess: () => settle(),
    onError: (caught) => settle(caught),
  });

  const reorder = useMutation({
    mutationFn: (ordering: string[]) => {
      if (draftVersion === null) throw new Error("no editable draft");
      return imagesApi.reorderImages(productId, {
        expected_version: draftVersion,
        ordering,
      });
    },
    onSuccess: () => settle(),
    onError: (caught) => settle(caught),
  });

  const remove = useMutation({
    mutationFn: (imageId: string) => {
      if (draftVersion === null) throw new Error("no editable draft");
      return imagesApi.removeImage(productId, imageId, draftVersion);
    },
    onSuccess: () => settle(),
    onError: (caught) => settle(caught),
  });

  const busy = setEnabled.isPending || reorder.isPending || remove.isPending;

  return { setEnabled, reorder, remove, busy, error, clearError: () => setError(null) };
}

/** The ordering to submit after moving one image within its colour.
 *
 * The whole list is sent, not just the pair that swapped: display_order is
 * assigned from the position in this array, so a partial list would renumber
 * some images and leave others on stale positions.
 */
export function orderingAfterMove(
  images: ProductImage[],
  imageId: string,
  direction: -1 | 1,
): string[] | null {
  const ordered = [...images].sort(
    (a, b) => a.display_order - b.display_order || a.version - b.version,
  );
  const index = ordered.findIndex((image) => image.id === imageId);
  const target = index + direction;
  if (index === -1 || target < 0 || target >= ordered.length) return null;

  const moved = ordered[index]!;
  const displaced = ordered[target]!;
  ordered[index] = displaced;
  ordered[target] = moved;
  return ordered.map((image) => image.id);
}

export function conflictNotice(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.isStale) {
    return "This draft changed somewhere else, so nothing was applied. The latest images have been reloaded — try again.";
  }
  return null;
}
